import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { Button } from "reactstrap";
import { withTranslation } from "react-i18next";
import authUserPermissionService from "../../../../services/AuthUserPermissionService";
import authUserService from "../../../../services/AuthUserService";
import InactivePerfilModal from "./InactivePerfilModal";
import Loading from "components/Common/Loading";
import DynamicMaterialTable from "components/Common/MaterialTableContainer";
import useTableZoom from "components/Common/useTableZoom";
import PermissionsModal from "./PermissionsModal";
import { PencilSimple, Trash } from "@phosphor-icons/react";
import { IconButton, Tooltip } from "@mui/material";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
} from "components/Common/MaterialTableStyles";

const PerfilUsuarios = () => {
	// ========== CONFIGURAÇÃO DE LARGURAS MÍNIMAS E MÁXIMAS DAS COLUNAS ==========
	const COLUMN_WIDTHS = {
		name: { min: 200 },                    // Largura mínima da coluna "Nome" (sem máximo - cresce livremente)
		permissions: { min: 180, max: 200 },   // Largura mín/máx da coluna "Ações do perfil" (em pixels)
		actions: { min: 100, max: 100 },       // Largura mín/máx da coluna "Ações" (em pixels)
	};
	// ================================================================

	const [data, setData] = useState([]);
	const [authUserData, setAuthUserData] = useState([]);
	const [showInactiveModal, setShowInactiveModal] = useState(false);
	const [showPermissionsModal, setShowPermissionsModal] = useState(false);
	const [modalData, setModalData] = useState(null);
	const [activePerfilModal, setactivePerfilModal] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [permissions, setPermissions] = useState([]);

	const navigate = useNavigate();

	// Usar o hook customizado para zoom e cálculos de largura
	const { zoomLevel, getColumnWidth } = useTableZoom(COLUMN_WIDTHS);

	document.title = "Senda | Perfil Usuários";

	const columns = useMemo(
		() => [
			{
				header: "Nome",
				accessorKey: "name",
				filterFn: "contains",
				widthPixels: getColumnWidth('name'),
				minWidthPixels: COLUMN_WIDTHS.name.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
				Cell: ({ row }) => {
					let item = row.original;
					return (
						<div className="d-flex" style={{ width: "100%" }}>
							<div
								key={item.id}
								className="w-100 d-flex align-items-center px-4"
							>
								<span className="fw-bold fs-5">
									{item.name}
								</span>
							</div>
						</div>
					);
				},
			},
			{
				header: <span>Ações do perfil</span>,
				accessorKey: "permissions",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('permissions'),
				minWidthPixels: COLUMN_WIDTHS.permissions.min,
				maxWidthPixels: COLUMN_WIDTHS.permissions.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: ({ row }) => (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: "100%",
							height: "100%",
						}}
					>
						<Button
							style={{
								background: "none",
								padding: "8px",
								cursor: "pointer",
								color: "black",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 2,
								fontSize: "12px",
								border: "1px solid #ccc",
								borderRadius: "4px",
							}}
							onClick={() => viewPermissions(row.original)}
						>
							Ver ações do perfil
						</Button>
					</div>
				),
			},
			{
				header: <span>Ações</span>,
				accessorKey: "actions",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('actions'),
				minWidthPixels: COLUMN_WIDTHS.actions.min,
				maxWidthPixels: COLUMN_WIDTHS.actions.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => (
					<div style={{ 
						display: "flex", 
						flexWrap: "wrap",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
						gap: "2px",
					}}>
						<Tooltip title="Editar" placement="top">
							<IconButton
								style={{
									background: "none",
									border: "none",
									padding: "1px",
									cursor: "pointer",
									color: "black",
									minWidth: "auto",
									width: "auto",
									height: "auto",
								}}
								onClick={() => editarItem(row.original)}
							>
								<PencilSimple size={14} />
							</IconButton>
						</Tooltip>
						<Tooltip title="Excluir" placement="top">
							<IconButton
								style={{
									background: "none",
									border: "none",
									padding: "1px",
									cursor: "pointer",
									color: "black",
									minWidth: "auto",
									width: "auto",
									height: "auto",
								}}
								onClick={() => excluirItem(row.original)}
							>
								<Trash size={14} />
							</IconButton>
						</Tooltip>
					</div>
				),
			},
		],
		[authUserData, zoomLevel], // Adicionado zoomLevel como dependência
	);

	const editarItem = (rowData) => {
		navigate(`/form-perfil-usuario/${rowData.id}`);
	};

	const excluirItem = (rowData) => {
		setModalData(rowData);
		setShowInactiveModal(true);
	};

	const viewPermissions = (rowData) => {
		const userPermissions = authUserData.filter(
			(data) => data?.permission?.id === rowData.id,
		);
		userPermissions.sort((a, b) => {
			if (a.permission.name < b.permission.name) return -1;
			if (a.permission.name > b.permission.name) return 1;
			return 0;
		});

		setPermissions(userPermissions);
		setShowPermissionsModal(true);
	};

	const getPermissions = async () => {
		try {
			const response = await authUserPermissionService.get();
			setData(response);
		} catch (error) {
			console.log(error);
		}
	};

	const getActions = async () => {
		try {
			const response = await authUserService.get();
			setAuthUserData(response);
		} catch (error) {
			console.log(error);
		}
	};

	function onCloseInactiveModal() {
		setShowInactiveModal(false);
	}

	function onClosePermissionsModal() {
		setShowPermissionsModal(false);
	}

	const inactivatePerfil = async () => {
		try {
			if (modalData) {
				const response = await authUserPermissionService.put(
					modalData.id,
					{
						active: 0,
					},
				);
			}
		} catch (error) {
			console.log(error);
		}
		setShowInactiveModal(false);
		setactivePerfilModal(false);
	};

	function onDeletePerfil() {
		if (activePerfilModal) {
			setactivePerfilModal(false);
		} else {
			setactivePerfilModal(true);
		}
	}

	const getAll = async () => {
		setIsLoading(true);
		await getActions();
		await getPermissions();
		setIsLoading(false);
	};

	useEffect(() => {
		getAll();
	}, [activePerfilModal]);

	useEffect(() => {
		inactivatePerfil();
	}, [activePerfilModal]);

	return (
		<Loading isLoading={isLoading}>
			<div className="page-content">
				<Breadcrumbs
					botaoLink={"/form-perfil-usuario"}
					botao={"Adicionar Perfil"}
					title="Perfil de Usuários"
				/>

				<div
					className="container-fluid"
					style={{
						backgroundColor: "white",
						padding: 20,
						borderRadius: 10,
						marginTop: 25,
					}}
				>
					<DynamicMaterialTable columns={columns} data={data} />
				</div>

				<InactivePerfilModal
					show={showInactiveModal}
					onCloseClick={onCloseInactiveModal}
					onDeleteClick={onDeletePerfil}
					nomePerfil={modalData}
				/>

				<PermissionsModal
					show={showPermissionsModal}
					onCloseClick={onClosePermissionsModal}
					permissions={permissions}
				/>
			</div>
		</Loading>
	);
};

PerfilUsuarios.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default withTranslation()(PerfilUsuarios);
